import { Typography, Spin } from "antd";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import eventBus from "src/common/eventBus";
import { Button } from "src/components";
import { Form, FormDatePicker, FormNumber, FormSelect, FormText, FormCheckBox } from "src/components/Form";
import api from "src/config/api";
import "./ContractStyle.css";
import ContractResponsibilities from "./ContractResponsibilities";

const EditContract = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState();
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBike, setSelectedBike] = useState(null);
  const [bikeMap, setBikeMap] = useState({});
  const navigate = useNavigate();
  const sigPad = useRef(null);
  const returnSigPad = useRef(null);
  const contractRef = useRef(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bikes for dropdown
        const bikesResponse = await api.get('bikes', { params: { limit: 100, status: "IN" } });
        if (bikesResponse.data && bikesResponse.data.data) {
          const bikeData = bikesResponse.data.data;
          const bikeOptions = bikeData.map(bike => ({
            label: `${bike.bikeName} (${bike.licenseNumber || 'No License'})`,
            value: bike.id
          }));
          
          // Create a map of bike IDs to bike objects for quick lookup
          const bikeLookup = {};
          bikeData.forEach(bike => {
            bikeLookup[bike.id] = bike;
          });
          
          setBikes(bikeOptions);
          setBikeMap(bikeLookup);
        }
        
        // Fetch contract data
        const contractResponse = await api.get(`contracts/${id}`);
        const contractData = contractResponse.data;
        setFormData(contractData);
        
        // Set selected bike based on contract data
        if (contractData.bikeId && bikeMap[contractData.bikeId]) {
          setSelectedBike(bikeMap[contractData.bikeId]);
        }
      } catch (error) {
        eventBus.emit("dialog", {
          type: "error",
          message: error.response?.data?.message || "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  useEffect(() => {
    // Set selected bike when formData and bikeMap are available
    if (formData?.bikeId && Object.keys(bikeMap).length > 0) {
      setSelectedBike(bikeMap[formData.bikeId]);
    }
  }, [formData, bikeMap]);

  const handleSubmit = async (data) => {
    try {
      // Add signature data if exists
      if (sigPad.current && !sigPad.current.isEmpty()) {
        data.signature = sigPad.current.toDataURL();
      }
      
      // Add return signature data if exists
      if (returnSigPad.current && !returnSigPad.current.isEmpty()) {
        data.returnSignature = returnSigPad.current.toDataURL();
        data.returnDate = new Date().toISOString();
        data.status = 'completed';
      }

      await api.put(`contracts/${id}`, data);
      eventBus.emit("dialog", {
        type: "success",
        message: "Contract updated successfully!",
      });
      setTimeout(() => {
        navigate(`/contracts/${id}`);
        eventBus.emit("dialog", {
          open: false,
        });
      }, 2000);
    } catch (error) {
      eventBus.emit("dialog", {
        type: "error",
        message: error.response?.data?.message || "Something wrong",
      });
    }
  };
  
  const handleBikeChange = (bikeId) => {
    setSelectedBike(bikeMap[bikeId]);
  };

  const clearSignature = () => {
    if (sigPad.current) {
      sigPad.current.clear();
    }
  };
  
  const clearReturnSignature = () => {
    if (returnSigPad.current) {
      returnSigPad.current.clear();
    }
  };
  
  // Initialize signature from existing data
  const loadSignatureFromData = () => {
    if (!formData?.signature || !sigPad.current) return;
    
    const img = new Image();
    img.onload = () => {
      const canvas = sigPad.current;
      const ctx = canvas._canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas._canvas.width, canvas._canvas.height);
    };
    img.src = formData.signature;
  };

  // Initialize return signature from existing data
  const loadReturnSignatureFromData = () => {
    if (!formData?.returnSignature || !returnSigPad.current) return;
    
    const img = new Image();
    img.onload = () => {
      const canvas = returnSigPad.current;
      const ctx = canvas._canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas._canvas.width, canvas._canvas.height);
    };
    img.src = formData.returnSignature;
  };

  useEffect(() => {
    if (formData?.signature && sigPad.current) {
      loadSignatureFromData();
    }
    if (formData?.returnSignature && returnSigPad.current) {
      loadReturnSignatureFromData();
    }
  }, [formData, sigPad.current, returnSigPad.current]);
  
  if (loading) {
    return <Spin tip="Loading data..." />;
  }

  return (
    <>
      <div className="page-header">
        <Typography.Title level={4}>Edit Contract</Typography.Title>
        <div>
          <Button onClick={() => navigate("/contracts")} style={{ marginRight: 10 }}>
            Back to List
          </Button>
        </div>
      </div>
      
      {formData && (
        <div className="contract-container" ref={contractRef}>
          <div className="contract-header">
            <div className="logo">Just Bikes</div>
            <div className="subtitle">Motorbike, bicycle rental & tours</div>
            <div className="contact">Phone/WhatsApp: +84 77 7507 323</div>
            <div className="hours">OPEN 9AM TO 5PM</div>
          </div>

          <h2 className="contract-title">Rental Contract</h2>

          <Form onSubmit={handleSubmit} layout="horizontal" className="contract-form" defaultValues={formData}>
            <div className="form-row">
              <div className="form-label">Name:</div>
              <FormText 
                name="customerName"
                placeholder=""
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">Nationality:</div>
              <FormText
                name="nationality"
                placeholder=""
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">Phone/WhatsApp:</div>
              <FormText
                name="phoneWhatsapp"
                placeholder=""
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">Address/Hotel:</div>
              <FormText
                name="addressHotel"
                placeholder=""
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row bike-selection">
              <div className="form-label">Bike:</div>
              <FormSelect
                name="bikeId"
                placeholder="Select a bike"
                options={bikes}
                required
                noLabel
                className="form-input"
                onChange={handleBikeChange}
              />
            </div>

            <div className="form-row">
              <div className="form-label">Number:</div>
              <div className="form-input license-display">
                {selectedBike?.licenseNumber || formData?.licenseNumber || ''}
              </div>
            </div>

            <div className="form-row">
              <div className="form-label">Start date and time:</div>
              <FormDatePicker
                placeholder=""
                format="DD/MM/YYYY HH:mm"
                showTime
                name="startDate"
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">End date and time:</div>
              <FormDatePicker
                placeholder=""
                format="DD/MM/YYYY HH:mm"
                showTime
                name="endDate"
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">Price:</div>
              <FormNumber 
                placeholder="" 
                name="price"
                noLabel
                className="form-input"
              />
            </div>

            <div className="form-row">
              <div className="form-label">Deposit:</div>
              <FormNumber 
                placeholder="" 
                name="deposit"
                noLabel
                className="form-input"
              />
            </div>
            <div className="form-row">
              <div className="form-label">Deposit Type:</div>
              <div className="deposit-options">
                <FormCheckBox name="depositPassport" label="Passport" noLabel />
                <FormCheckBox name="depositIDCard" label="ID card" noLabel />
                <FormCheckBox name="depositDrivingLicense" label="Driving license" noLabel />
                <FormCheckBox name="depositCash" label="Cash" noLabel />
              </div>
            </div>

            <div className="form-row">
              <div className="form-label">Helmets:</div>
              <div className="helmet-inputs">
                <div className="helmet-input">
                  <span className="helmet-label">Large</span>
                  <FormNumber
                    name="helmetLarge"
                    placeholder=""
                    noLabel
                    min={0}
                    className="qty-input"
                  />
                </div>
                <div className="helmet-input">
                  <span className="helmet-label">Small</span>
                  <FormNumber
                    name="helmetSmall"
                    placeholder=""
                    noLabel
                    min={0}
                    className="qty-input"
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-label">Extras:</div>
              <div className="extras-options">
                <div className="extras-row">
                  <div className="checkbox-item">
                    <FormCheckBox name="phoneHolder" label="Phone holder" noLabel />
                  </div>
                  <div className="checkbox-item">
                    <FormCheckBox name="bicycleLock" label="Bicycle lock" noLabel />
                  </div>
                  <div className="checkbox-item">
                    <FormCheckBox name="bicycleLights" label="Bicycle lights" noLabel />
                  </div>
                </div>
              </div>
            </div>

            <ContractResponsibilities />

            <div className="signature-section">
              <div className="signature-container">
                <div className="signature-label">Signature:</div>
                <div className="signature-pad-container">
                  <SignatureCanvas
                    ref={sigPad}
                    penColor="black"
                    canvasProps={{
                      className: "signature-pad",
                      width: 300,
                      height: 100
                    }}
                  />
                </div>
                <Button type="default" onClick={clearSignature} size="small">
                  Clear Signature
                </Button>
              </div>
              
              <div className="date-container">
                <div className="date-label">Date:</div>
                <div className="date-value">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="return-section">
              <h3>Bike Return</h3>
              <div className="form-row">
                <div className="form-label">Return Status:</div>
                <FormSelect
                  name="returnStatus"
                  placeholder="Select status"
                  options={[
                    { label: "Good condition", value: "good" },
                    { label: "Minor damages", value: "minor_damages" },
                    { label: "Major damages", value: "major_damages" }
                  ]}
                  noLabel
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-label">Additional Charges:</div>
                <FormNumber
                  name="additionalCharges"
                  placeholder="0"
                  noLabel
                  min={0}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <div className="form-label">Notes:</div>
                <FormText
                  name="returnNotes"
                  placeholder="Any notes about the return condition"
                  noLabel
                  className="form-input"
                />
              </div>
              
              <div className="signature-section">
                <div className="signature-container">
                  <div className="signature-label">Return Signature:</div>
                  <div className="signature-pad-container">
                    <SignatureCanvas
                      ref={returnSigPad}
                      penColor="black"
                      canvasProps={{
                        className: "signature-pad",
                        width: 300,
                        height: 100
                      }}
                    />
                  </div>
                  <Button type="default" onClick={clearReturnSignature} size="small">
                    Clear Signature
                  </Button>
                </div>
                
                <div className="date-container">
                  <div className="date-label">Return Date:</div>
                  <div className="date-value">
                    {new Date().toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="contract-footer">
              <Button type="primary" htmlType="submit" className="submit-button">
                Update Contract
              </Button>
            </div>
          </Form>
        </div>
      )}
    </>
  );
};

export default EditContract;
